import { useState, useMemo } from 'react';
import {
  getDisplayCountry,
  formatExactMoney,
  formatUsd,
} from '../data/countries';
import { PORTFOLIO_INVESTMENTS_PAGE_SIZE } from '../config/constants';
import useListPagination from '../hooks/useListPagination';
import PaginationBar from '../components/shared/PaginationBar';
import { getUserDisplayName, getUserInitials, getUserAvatarUrl } from '../utils/properties';
import {
  getInvestmentCostUsd,
  getInvestmentCurrentValueUsd,
  sumWalletDividendsUsd,
  resolveInvestmentProperty,
  getPortfolioGeographySummary,
  groupPortfolioHoldings,
} from '../utils/investments';
import { findPropertyById, getPropertyCardImage } from '../utils/properties';
import CardOnFilePanel from '../components/investment/CardOnFilePanel';
import PageRefinedHero from '../components/layout/PageRefinedHero';

const PortfolioStatLabel = ({ label, help }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="stat-card-label-row">
      <label>{label}</label>
      <button
        type="button"
        className={`stat-help-trigger${open ? " is-open" : ""}`}
        aria-label={`More about ${label}`}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
      >
        <i className="fas fa-circle-question" aria-hidden="true"></i>
        <span className="stat-help-tooltip" role="tooltip">{help}</span>
      </button>
    </div>
  );
};

const formatWalletTxType = (type) => {
  const t = String(type || "").toLowerCase();
  if (t === "management_fee") return "Management fee";
  if (t === "deposit") return "Deposit";
  if (t === "investment") return "Investment";
  if (t === "sell_primary_return") return "Sell (return to offering)";
  if (t === "rental_payout" || t === "dividend" || t === "distribution") return "Distribution";
  if (t === "marketplace_buy") return "Marketplace buy";
  if (t === "marketplace_sell") return "Marketplace sell";
  return type || "Transaction";
};

const toDateLabel = (value) => {
  if (!value) return "—";
  const d = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toISOString().split("T")[0];
};

const getDashboardTabHero = ({
  tab,
  holdings,
  propertyCount,
  transactionRows,
  totalInvested,
  dividendsPaid,
  kycDone,
  email,
  country,
}) => {
  const propertyCountLabel = `${propertyCount} propert${propertyCount === 1 ? "y" : "ies"}`;
  const txCountLabel = `${transactionRows.length} transaction${transactionRows.length === 1 ? "" : "s"}`;

  const heroes = {
    portfolio: {
      badge: "Portfolio",
      title: "My",
      titleAccent: "portfolio",
      subtitle: "Track holdings, returns, and property investments in one place.",
      benefits: [
        { icon: "fas fa-chart-pie", title: propertyCountLabel, subtitle: "Active investments" },
        { icon: "fas fa-coins", title: formatUsd(totalInvested), subtitle: "Total invested" },
        { icon: "fas fa-hand-holding-usd", title: formatUsd(dividendsPaid), subtitle: "Dividends earned" },
      ],
    },
    transactions: {
      badge: "Wallet activity",
      title: "Transaction",
      titleAccent: "history",
      subtitle: "Your investment purchases, deposits, distributions, and marketplace activity on CoOwn.",
      benefits: [
        { icon: "fas fa-wallet", title: txCountLabel, subtitle: "Recorded on your account" },
        { icon: "fas fa-exchange-alt", title: "Deposits & invests", subtitle: "Funding and share purchases" },
        { icon: "fas fa-receipt", title: "Distributions", subtitle: "Rental payouts when paid" },
      ],
    },
    tax: {
      badge: "Tax center",
      title: "Tax",
      titleAccent: "documents",
      subtitle: "Official tax forms are generated after each tax year based on your investments. Download when marked available.",
      benefits: [
        { icon: "fas fa-file-invoice-dollar", title: "1099 & K-1", subtitle: "Standard investor forms" },
        { icon: "fas fa-calendar-alt", title: "Annual summary", subtitle: "Year-end investment report" },
        { icon: "fas fa-download", title: "Secure download", subtitle: "Available when tax year closes" },
      ],
    },
    notifications: {
      badge: "Alerts",
      title: "Account",
      titleAccent: "notifications",
      subtitle: `Email notifications are sent to ${email || "your registered email"} for investments, dividends, and KYC updates.`,
      benefits: [
        { icon: "fas fa-bell", title: "Investment alerts", subtitle: "Purchases and confirmations" },
        { icon: "fas fa-envelope", title: "Email delivery", subtitle: "Sent to your registered address" },
        { icon: "fas fa-user-shield", title: kycDone ? "KYC verified" : "KYC pending", subtitle: kycDone ? "Ready to invest" : "Complete verification" },
      ],
    },
    kyc: {
      badge: "Verification",
      title: "KYC",
      titleAccent: "status",
      subtitle: kycDone
        ? "You are cleared to invest on CoOwn properties."
        : "Complete identity verification to unlock investing on live properties.",
      benefits: [
        { icon: "fas fa-user-shield", title: kycDone ? "Verified" : "Pending", subtitle: kycDone ? "Investing enabled" : "Action required" },
        { icon: "fas fa-id-card", title: "Identity check", subtitle: "Required for compliance" },
        { icon: "fas fa-lock", title: "Secure review", subtitle: "Admin-reviewed submission" },
      ],
    },
    settings: {
      badge: "Account",
      title: "Profile",
      titleAccent: "settings",
      subtitle: "Manage your contact details, funding methods, and CoOwn account preferences.",
      benefits: [
        { icon: "fas fa-user", title: "Account details", subtitle: "Name and email on file" },
        { icon: "fas fa-credit-card", title: "Payment methods", subtitle: "Card on file for deposits" },
        { icon: "fas fa-globe", title: getDisplayCountry(country).name, subtitle: "Country preference in nav" },
      ],
    },
  };

  return heroes[tab] || heroes.portfolio;
};

const DashboardPage = ({ user, isLoggedIn = false, investments, properties, onSell, onViewProperty, country, kycDone, onOpenKYC, setPage, onAddFunds, addToast, walletTransactions = [], stripePublishableKey = "" }) => {
  const [tab, setTab] = useState("portfolio");
  const holdings = isLoggedIn && Array.isArray(investments) ? investments : [];
  const portfolioRows = useMemo(
    () => groupPortfolioHoldings(holdings, properties),
    [holdings, properties]
  );
  const propertyCount = portfolioRows.length;
  const portfolioPaginationKey = `${portfolioRows.length}-${holdings.length}-${country}`;
  const {
    items: portfolioPageItems,
    page: portfolioPage,
    totalPages: portfolioTotalPages,
    total: portfolioTotal,
    startIndex: portfolioStart,
    endIndex: portfolioEnd,
    hasPrev: portfolioHasPrev,
    hasNext: portfolioHasNext,
    goToPage: portfolioGoToPage
  } = useListPagination(portfolioRows, portfolioPaginationKey, PORTFOLIO_INVESTMENTS_PAGE_SIZE);
  const walletRows = isLoggedIn && Array.isArray(walletTransactions) ? walletTransactions : [];
  const totalInvested = holdings.reduce((s, i) => s + getInvestmentCostUsd(i), 0);
  const currentValue = holdings.reduce((s, i) => s + getInvestmentCurrentValueUsd(i), 0);
  const dividendsPaid = sumWalletDividendsUsd(walletRows);
  const geography = getPortfolioGeographySummary(holdings, properties);
  const c = getDisplayCountry(country);
  const displayName = getUserDisplayName(user);
  const email = user?.email || "";
  const initials = getUserInitials(user);
  const avatarUrl = getUserAvatarUrl(user);

  const menuItems = [
    { id: "portfolio", icon: "fa-chart-pie", label: "Portfolio" },
    { id: "transactions", icon: "fa-wallet", label: "Transactions" },
    { id: "tax", icon: "fa-file-invoice-dollar", label: "Tax Documents" },
    { id: "notifications", icon: "fa-bell", label: "Notifications" },
    { id: "kyc", icon: "fa-user-shield", label: "KYC Status" },
    { id: "settings", icon: "fa-cog", label: "Settings" },
  ];

  const investmentRows = holdings.flatMap((inv) => {
    const p = resolveInvestmentProperty(properties, inv);
    const cost = getInvestmentCostUsd(inv);
    return [{
      id: `buy-${inv.propertyId}`,
      date: toDateLabel(inv.purchaseDate),
      type: "Investment",
      property: p?.title || "Property",
      amount: -cost,
    }];
  });

  const walletTxRows = walletRows.map((tx, index) => {
    const created = tx.createdAt?.toDate ? tx.createdAt.toDate() : new Date(tx.createdAt || 0);
    const prop = tx.propertyId ? findPropertyById(properties, tx.propertyId) : null;
    return {
      id: tx.id || `wtx-${index}`,
      date: toDateLabel(tx.createdAt),
      type: formatWalletTxType(tx.type),
      property: prop?.title || (tx.propertyId ? String(tx.propertyId) : "—"),
      amount: Number(tx.amountUsd) || 0,
    };
  });

  const transactionRows = [...investmentRows, ...walletTxRows].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  const taxDocs = [
    { id: "1099", name: "Form 1099-DIV (Dividends)", year: "2025", status: holdings.length ? "available" : "pending" },
    { id: "k1", name: "Schedule K-1 (Partnership)", year: "2025", status: "pending" },
    { id: "summary", name: "Annual investment summary", year: "2025", status: holdings.length ? "available" : "pending" },
  ];

  const handleTaxDownload = (doc) => {
    if (doc.status !== "available") {
      addToast(`${doc.name} will be ready after you have completed investments for the tax year.`, "error");
      return;
    }
    addToast(`Preparing ${doc.name} — you will receive it by email when ready.`);
  };

  const tabHero = getDashboardTabHero({
    tab,
    holdings,
    propertyCount,
    transactionRows,
    totalInvested,
    dividendsPaid,
    kycDone,
    email,
    country,
  });

  if (!isLoggedIn) {
    return (
      <div className="coown-page coown-page--hero">
        <PageRefinedHero
          badge="Portfolio"
          title="My"
          titleAccent="portfolio"
          subtitle="Sign in to view investments, transactions, tax documents, and wallet activity."
          benefits={[
            { icon: "fas fa-chart-pie", title: "Portfolio", subtitle: "Holdings and performance" },
            { icon: "fas fa-wallet", title: "Transactions", subtitle: "Deposits and investments" },
            { icon: "fas fa-file-invoice-dollar", title: "Tax documents", subtitle: "Forms when available" },
          ]}
        />
        <div className="coown-page-body coown-page-body--narrow">
          <div className="coown-page-empty">
            <i className="fas fa-user coown-page-empty__icon" aria-hidden="true"></i>
            <h3>Sign in to view your portfolio</h3>
            <p>Your investments, transactions, and wallet activity are only available while you are signed in.</p>
            <button type="button" className="btn btn-primary" onClick={() => setPage("login")}>Log in</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coown-page coown-page--hero">
      <PageRefinedHero {...tabHero} />
      <div className="coown-page-body">
      <div className="dashboard-grid">
        <aside className="sidebar">
          <div className="user-profile">
            <div className={`user-avatar${initials || avatarUrl ? "" : " user-avatar--guest"}`} style={avatarUrl ? { overflow: "hidden" } : undefined}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                initials || <i className="fas fa-user" aria-hidden="true"></i>
              )}
            </div>
            <div className="user-name">{displayName}</div>
            <div className="user-email">{email || "Sign in to sync your profile"}</div>
          </div>
          <ul className="sidebar-menu">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button type="button" className={tab === item.id ? "active" : ""} onClick={() => setTab(item.id)}>
                  <i className={`fas ${item.icon}`}></i> {item.label}
                </button>
              </li>
            ))}
          </ul>
          {setPage && (
            <div style={{ marginTop: "1.25rem", paddingTop: "1rem", borderTop: "1px solid var(--border)" }}>
              <button type="button" className="btn btn-outline btn-sm" style={{ width: "100%", marginBottom: "0.5rem" }} onClick={() => setPage("list-property")}>
                <i className="fas fa-home"></i> List Property
              </button>
              <button type="button" className="btn btn-outline btn-sm" style={{ width: "100%" }} onClick={() => setPage("agent-dashboard")}>
                <i className="fas fa-list-alt"></i> My Listings
              </button>
            </div>
          )}
        </aside>
        <div className="main-content">
          {tab === "portfolio" && (
            <>
              <div className="stats-row">
                <div className="stat-card">
                  <PortfolioStatLabel
                    label="Total Invested"
                    help="Total amount you paid for your shares (cost basis), including fees recorded at purchase."
                  />
                  <value>{formatUsd(totalInvested)}</value>
                </div>
                <div className="stat-card">
                  <PortfolioStatLabel
                    label="Current Value"
                    help="Same as invested for now. When live pricing is available, this will reflect the latest value of your holdings."
                  />
                  <value>{formatUsd(currentValue)}</value>
                </div>
                <div className="stat-card">
                  <PortfolioStatLabel label="Properties Owned" help={geography.help} />
                  <value>{propertyCount}</value>
                </div>
                <div className="stat-card">
                  <PortfolioStatLabel
                    label="Dividends Earned"
                    help={
                      dividendsPaid > 0
                        ? "Total rental distributions paid to your CoOwn wallet (from recorded transactions)."
                        : "No dividend payouts recorded yet. Amounts appear here after distributions are paid to your wallet."
                    }
                  />
                  <value>{formatUsd(dividendsPaid)}</value>
                </div>
              </div>
              <h3 style={{marginBottom:"1rem"}}>My Investments</h3>
              {holdings.length === 0 ? (
                <div style={{textAlign:"center",padding:"3rem",color:"var(--text-light)"}}>
                  <i className="fas fa-home" style={{fontSize:"3rem",marginBottom:"1rem",display:"block"}}></i>
                  No investments yet. <button type="button" className="btn btn-primary btn-sm" style={{marginTop:"1rem"}} onClick={() => setPage("home")}>Browse properties</button>
                </div>
              ) : (
                <>
                <table className="investment-table">
                  <thead>
                    <tr><th>Property</th><th>Shares</th><th>Invested</th><th>Current Value</th><th>Return</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {portfolioPageItems.map((group) => {
                      const returnPct = "0.0";
                      const thumbUrl = getPropertyCardImage(group.p);
                      const sellTarget = { lots: group.investments, propertyId: group.propertyId };
                      return (
                        <tr key={group.propertyId}>
                          <td>
                            <button
                              type="button"
                              className="property-cell property-cell-link"
                              onClick={() => onViewProperty?.(group.p)}
                              title={`View ${group.p.title} on the site`}
                            >
                              <div className="property-thumb" style={{backgroundImage:`url(${thumbUrl})`}}></div>
                              <div className="property-cell-info">
                                <h4>{group.p.title}</h4>
                                <span>{group.p.location}</span>
                                {group.lotCount > 1 ? (
                                  <span style={{ display: "block", fontSize: "0.78rem", color: "var(--text-light)", marginTop: "0.2rem" }}>
                                    {group.lotCount} separate purchases
                                  </span>
                                ) : null}
                              </div>
                            </button>
                          </td>
                          <td>{group.shares}</td>
                          <td>{formatUsd(group.investedUsd)}</td>
                          <td>{formatUsd(group.currentValueUsd)}</td>
                          <td style={{color:returnPct>=0?"var(--success)":"var(--danger)",fontWeight:600}}>{returnPct>=0?"+":""}{returnPct}%</td>
                          <td><span className="status-badge status-active">Active</span></td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              onClick={() => onSell(sellTarget)}
                              title="Sell shares from this property"
                            >
                              Sell
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <PaginationBar
                  page={portfolioPage}
                  totalPages={portfolioTotalPages}
                  total={portfolioTotal}
                  startIndex={portfolioStart}
                  endIndex={portfolioEnd}
                  hasPrev={portfolioHasPrev}
                  hasNext={portfolioHasNext}
                  onPageChange={portfolioGoToPage}
                  itemLabel="properties"
                  compact
                />
                </>
              )}
            </>
          )}
          {tab === "transactions" && (
            <>
              {transactionRows.length === 0 ? (
                <p style={{color:"var(--text-light)"}}>No transactions yet.</p>
              ) : (
                <table className="investment-table">
                  <thead><tr><th>Date</th><th>Type</th><th>Property</th><th>Amount</th></tr></thead>
                  <tbody>
                    {transactionRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.date}</td>
                        <td>{row.type}</td>
                        <td>{row.property}</td>
                        <td style={{fontWeight:600,color:row.amount<0?"var(--primary)":"var(--success)"}}>
                          {row.amount < 0 ? "−" : "+"}{formatUsd(Math.abs(row.amount))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
          {tab === "tax" && (
            <>
              {taxDocs.map((doc) => (
                <div key={doc.id} className="agent-card" style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:"1rem"}}>
                  <div>
                    <h4 style={{marginBottom:"0.25rem"}}>{doc.name}</h4>
                    <span style={{fontSize:"0.85rem",color:"var(--text-light)"}}>Tax year {doc.year}</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:"0.75rem"}}>
                    <span className={`status-badge status-${doc.status === "available" ? "approved" : "pending"}`}>
                      {doc.status === "available" ? "Available" : "Pending"}
                    </span>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => handleTaxDownload(doc)}>
                      <i className="fas fa-download"></i> Download
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
          {tab === "notifications" && (
            <>
              <div className="agent-card">
                <h4><i className="fas fa-bell" style={{color:"var(--primary)",marginRight:"0.5rem"}}></i>Account alerts</h4>
                <p style={{color:"var(--text-light)",fontSize:"0.9rem",marginTop:"0.5rem"}}>Email notifications are sent to <strong>{email || "your registered email"}</strong> for investments, dividends, and KYC updates.</p>
              </div>
              {!kycDone && (
                <div className="agent-card" style={{borderLeft:"4px solid var(--warning)"}}>
                  <h4>Complete verification</h4>
                  <p style={{color:"var(--text-light)",fontSize:"0.9rem"}}>Finish KYC to unlock investing.</p>
                  <button type="button" className="btn btn-primary btn-sm" style={{marginTop:"0.75rem"}} onClick={onOpenKYC}>Verify now</button>
                </div>
              )}
            </>
          )}
          {tab === "kyc" && (
            <>
              <div className="detail-card">
                <p style={{marginBottom:"1rem"}}>Status: <span className={`status-badge status-${kycDone ? "approved" : "pending"}`}>{kycDone ? "Verified" : "Pending verification"}</span></p>
                {!kycDone && <button type="button" className="btn btn-primary" onClick={onOpenKYC}>Complete verification</button>}
                {kycDone && <p style={{color:"var(--text-light)"}}>You are cleared to invest on CoOwn properties.</p>}
              </div>
            </>
          )}
          {tab === "settings" && (
            <>
              <div className="detail-card">
                <div className="form-group"><label>Name</label><input readOnly value={displayName} /></div>
                <div className="form-group"><label>Email</label><input readOnly value={email} /></div>
                <div className="form-group"><label>Country preference</label><p style={{color:"var(--text-light)",fontSize:"0.9rem"}}>Use the country selector in the top navigation bar.</p></div>
                <button type="button" className="btn btn-primary btn-sm" onClick={onAddFunds}>Add funds</button>
              </div>
              <CardOnFilePanel stripePublishableKey={stripePublishableKey} addToast={addToast} />
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export { PortfolioStatLabel, formatWalletTxType };
export default DashboardPage;
